import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { LocationSource } from '../api/globalTypes'
import {
    selectLocationCityFilter,
    selectLocationCountryFilter,
    selectLocationCountyFilter,
    selectLocationIslandFilter,
    selectLocationQuarterFilter,
    selectLocationWiderAreaFilter,
    setFormFilter,
} from '../store/formFilters'

interface LocationData {
    country_id?: number
    county_id?: number | number[]
    city_id?: number | number[]
    wider_area_id?: number | number[]
    quarter_id?: number | number[]
    island_id?: number | number[]
}

type LocationField = { id?: number } | { id?: number }[] | null

/**
 * Hook to initialize location filters in store
 * Accepts any entity with nested location structure (e.g., property, contact, agent defaults)
 * Location form filters trigger useLocationValues hook to fetch correct location values which then populate the form inputs
 */
export const useInitializeLocationFilters = (entity?: LocationSource | null) => {
    const dispatch = useDispatch()

    const { countryId, countyId, cityId, widerAreaId, quarterId, islandId } = useSelector((state) => ({
        countryId: selectLocationCountryFilter(state),
        countyId: selectLocationCountyFilter(state),
        cityId: selectLocationCityFilter(state),
        widerAreaId: selectLocationWiderAreaFilter(state),
        quarterId: selectLocationQuarterFilter(state),
        islandId: selectLocationIslandFilter(state),
    }))

    const locationData = useMemo((): LocationData => {
        if (!entity) return {}

        const extractIds = (field?: LocationField) => {
            if (!field) return undefined
            return Array.isArray(field) ? field.map((item) => item.id).filter((id): id is number => id !== undefined) : field.id
        }

        return {
            country_id: entity.country?.id,
            county_id: extractIds(entity.county),
            city_id: extractIds(entity.city),
            wider_area_id: extractIds(entity.widerArea),
            quarter_id: extractIds(entity.quarter),
            island_id: extractIds(entity.island),
        }
    }, [entity])

    useEffect(() => {
        const hasExistingLocationFilters = countryId || countyId || cityId || widerAreaId || quarterId || islandId

        if (!entity || hasExistingLocationFilters) {
            return
        }

        if (locationData.country_id) {
            dispatch(setFormFilter({ context: 'location', field: 'country', value: locationData.country_id }))
        }

        if (locationData.county_id) {
            dispatch(setFormFilter({ context: 'location', field: 'county', value: locationData.county_id }))
        }

        if (locationData.city_id) {
            dispatch(setFormFilter({ context: 'location', field: 'city', value: locationData.city_id }))
        }

        if (locationData.wider_area_id) {
            dispatch(setFormFilter({ context: 'location', field: 'widerArea', value: locationData.wider_area_id }))
        }

        if (locationData.quarter_id) {
            dispatch(setFormFilter({ context: 'location', field: 'quarter', value: locationData.quarter_id }))
        }

        if (locationData.island_id) {
            dispatch(setFormFilter({ context: 'location', field: 'island', value: locationData.island_id }))
        }
    }, [dispatch, entity, locationData, countryId, countyId, cityId, widerAreaId, quarterId, islandId])
}
